/**
 * The Management screen's shared reads, as hooks.
 *
 * Gap A phase 2. The four queries these wrap used to live inside
 * `AdsManagementTab`, which is why every block that needed one of them had
 * to render inside that component — and why the same eleven blocks sat under
 * every category tab. Art's finding: "whichever tab you're on, the page looks
 * almost identical once you scroll past the top."
 *
 * Moving a block to its own tab therefore meant giving that tab the data, and
 * the cheap way to do that is a hook per read rather than prop-drilling or a
 * second fetch. Every hook keeps the EXACT query key the old inline
 * `useQuery` used, so react-query dedupes: Ad Review and the legacy body
 * asking for the ad monitor is still one network trip, not two.
 *
 * Each hook reads the window from `useManagementWindow` itself, so callers
 * pass only a tenant and cannot accidentally draw a card over a different
 * range than the page says (CORE-M-12).
 */
import { useQuery } from '@tanstack/react-query';

import {
  getAdMonitor,
  getChangeHistory,
  getConversionView,
  getStructure,
} from '@/services/googleAdsManagementService';

import { useManagementWindowDays } from './useManagementWindow';

/**
 * Google's ceiling on change history, mirroring MAX_WINDOW_DAYS in
 * backend/api/services/google_ads/change_history_service.py. The route
 * rejects anything larger by design ("rejected rather than silently
 * truncated"), so the one card that cannot honour a 90-day selection clamps
 * to this instead of 422-ing and reporting a read failure (CORE-M-12).
 */
export const CHANGE_HISTORY_MAX_DAYS = 30;

const SHARED = {
  enabled: true,
  staleTime: 60_000,
} as const;

/** Ad-level health for the selected window. */
export function useManagementAds(tenantId: string) {
  const days = useManagementWindowDays();
  return useQuery({
    queryKey: ['ga-ad-monitor', tenantId, days],
    queryFn: () => getAdMonitor(tenantId, days),
    enabled: Boolean(tenantId),
    staleTime: SHARED.staleTime,
  });
}

/** Conversion actions and their counts for the selected window. */
export function useManagementConversions(tenantId: string) {
  const days = useManagementWindowDays();
  return useQuery({
    queryKey: ['ga-conversion-view', tenantId, days],
    queryFn: () => getConversionView(tenantId, days),
    enabled: Boolean(tenantId),
    staleTime: SHARED.staleTime,
  });
}

/** Campaigns, ad groups and keywords for the selected window. */
export function useManagementStructure(tenantId: string) {
  const days = useManagementWindowDays();
  return useQuery({
    queryKey: ['ga-structure', tenantId, days],
    queryFn: () => getStructure(tenantId, days),
    enabled: Boolean(tenantId),
    staleTime: SHARED.staleTime,
  });
}

/**
 * Change history, clamped to what Google actually keeps.
 *
 * Returns the clamped window alongside the query so a caller can say why its
 * list is shorter than the page's selection instead of leaving the reader to
 * assume nothing happened.
 */
export function useManagementChanges(tenantId: string) {
  const days = useManagementWindowDays();
  const clampedDays = Math.min(days, CHANGE_HISTORY_MAX_DAYS);
  const query = useQuery({
    queryKey: ['ga-change-history', tenantId, clampedDays],
    queryFn: () => getChangeHistory(tenantId, clampedDays),
    enabled: Boolean(tenantId),
    staleTime: SHARED.staleTime,
  });
  return { ...query, clampedDays, pageDays: days };
}
